import { SxProps, Theme } from '@mui/material';
import { green } from '@mui/material/colors';
import { CellProps } from 'react-table';

import { EDITABLE_TABLE_MAX_ROWS_COUNT } from '../constants/shell';
import { variables } from '../contexts/ThemeProvider';
import { EditableTableModes } from '../types/forms';
import { IEditableCellProps, IStickyColumnStyleParams, ITableConfig, TPlaceholderCell } from '../types/tables';
import { IUser } from '../types/users';
import { getFullName } from './helpers';

export const getOriginal = <T extends object>(cellData: CellProps<T, unknown>) => cellData.row.original;

export const getCustomProps = <T extends object, V>(cellData: CellProps<T, V>): IEditableCellProps<T> | undefined =>
  cellData.getCustomProps ? cellData.getCustomProps() : undefined;

export const getColumnId = <T extends object>(cellData: CellProps<T, unknown>): string => cellData.column.id;

export const getColumnKeys = <T>({ config }: { config?: ITableConfig<T> }): Set<keyof T> => {
  const result: Set<keyof T> = new Set();

  config?.columns?.visible?.forEach((key) => {
    result.add(key as keyof T);
  });

  return result;
};

export const getPlaceholderItems = <T>({
  items,
  mode,
  config,
}: {
  items?: T[];
  mode?: EditableTableModes;
  config?: ITableConfig<T>;
}) => {
  const columnKeys = getColumnKeys({ config });

  const count = (() => {
    let result = EDITABLE_TABLE_MAX_ROWS_COUNT;

    if (Array.isArray(items)) {
      result = items.length < EDITABLE_TABLE_MAX_ROWS_COUNT ? EDITABLE_TABLE_MAX_ROWS_COUNT - items.length : 0;
    }

    return mode === EditableTableModes.Add ? --result : result;
  })();

  if (count > 0) {
    const dummyItem: TPlaceholderCell<T> = {} as TPlaceholderCell<T>;

    Array.from(columnKeys).forEach((key) => {
      dummyItem[key] = null;
    });

    return new Array<TPlaceholderCell<T>>(count).fill(dummyItem);
  }

  return [];
};

export const isPlaceholderRow = <T>(item: T) =>
  Object.values(item).every((val: string | number | boolean | null | undefined) => val === null);

export const getStickyColumnStyle = (params: IStickyColumnStyleParams): SxProps<Theme> => {
  const {
    theme: { palette, sass },
    position,
    border,
    width,
    minWidth,
    maxWidth,
    extraStyles = {},
  } = params;

  return {
    ...(position === 'left'
      ? {
          position: { xs: 'relative', sm: 'sticky' },
          left: { xs: 'auto', sm: 0 },
          borderRight: border || '1px solid #0000001F',
        }
      : {
          position: 'sticky',
          right: 0,
          borderLeft: border || `1px solid ${palette.divider}`,
          boxShadow: variables.tableShadow,
        }),
    ...(width && { width }),
    ...(minWidth && { minWidth }),
    ...(maxWidth && { maxWidth }),
    backgroundColor: sass.white,
    zIndex: position === 'left' ? { xs: 1, sm: 9 } : { xs: 10, sm: 8 },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: sass.white,
      zIndex: -1,
    },
    [`tr[data-has-menu="true"] &::before`]: {
      backgroundColor: palette.action.hover,
    },
    '.MuiTableRow-hover:hover &::before': {
      backgroundColor: palette.action.hover,
    },
    ...extraStyles,
  };
};

export const defaultTableStyles = (theme: Theme) => ({
  bodyCellStyle: {
    cursor: 'pointer',
  } as SxProps<Theme>,
  minWidthBodyCellStyle: {
    cursor: 'pointer',
    minWidth: 150,
  } as SxProps<Theme>,
  ellipsisContainer: {
    width: '100%',
    display: 'block',
    whiteSpace: 'nowrap',
    textAlign: 'start',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    color: theme.sass.ebony,
    maxWidth: 250,
  } as SxProps<Theme>,
  doubleLineEllipsisContainer: {
    display: '-webkit-box',
    lineClamp: 2,
    WebkitLineClamp: 2,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    boxOrient: 'vertical',
    WebkitBoxOrient: 'vertical',
    lineHeight: '1.2em',
    maxHeight: '2.4em',
    textAlign: 'start',
    color: theme.sass.ebony,
    minWidth: 150,
    maxWidth: 250,
  } as SxProps<Theme>,
  threeLinesEllipsisContainer: {
    display: '-webkit-box',
    lineClamp: 3,
    WebkitLineClamp: 3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    boxOrient: 'vertical',
    WebkitBoxOrient: 'vertical',
    lineHeight: '1.2em',
    maxHeight: '3.6em',
    textAlign: 'start',
    minWidth: 250,
    maxWidth: 250,
    flex: 1,
  } as SxProps<Theme>,
  highlightedCellStyle: {
    backgroundColor: `${green[50]} !important`,
    '&::before': {
      backgroundColor: `${green[50]} !important`,
    },
    '&:hover::before': {
      backgroundColor: `${green[50]} !important`,
    },
  } as SxProps<Theme>,
});

export const getDefaultStickyStyles = (theme: Theme, actionsWidth: number = 60) => ({
  stickyLeftCellStyle: getStickyColumnStyle({
    theme,
    position: 'left',
    extraStyles: {
      cursor: 'pointer',
      maxWidth: '100%',
      minWidth: 320,
    },
  }),
  actionsCellStyle: getStickyColumnStyle({
    theme,
    position: 'right',
    width: actionsWidth,
    maxWidth: actionsWidth,
    minWidth: actionsWidth,
    extraStyles: {
      textAlign: 'center',
      '.MuiTableHead-root &, .MuiTableBody-root &': {
        '.MuiSvgIcon-root': {
          position: 'relative',
          top: '5px',
        },
      },
    },
  }),
});

export const findFullNameByEmail = (userList: IUser[] | null | undefined, email?: string) => {
  const user = userList?.find((u) => u.email === email);
  return user ? getFullName(user.first_name, user.last_name) : '';
};
